import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  Treemap,
  Tooltip,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { generatePortfolioSummary } from "../utils/portfolioSlmSummary";

const CORR_CSV_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/correlation_matrix.csv";

const VOLS_JSON_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/vols.json";

const EXPECTED_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/expected_returns.json";

const DAILY_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

const DIVIDENDS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/dividends.json";

const SECTORS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/sectors.json";

const MACRO_CANDIDATE_URLS = [
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/macro_dimension_scores.json",
  "https://cdn.jsdelivr.net/gh/aport550/majorah-data@main/public/data/macro_dimension_scores.json",
];

const RISK_FREE_RATE = 0.04;
const TRADING_DAYS = 252;

function normalizeTicker(s) {
  return String(s || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.-]/g, "");
}

function normalizeDateString(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return s;

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatShortDate(dateStr) {
  const s = normalizeDateString(dateStr);
  if (!s) return "";
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatPrice(v) {
  if (!isFiniteNumber(v)) return "—";
  return `$${v.toFixed(0)}`;
}

function parseHoldings(text) {
  const lines = String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const rows = [];
  for (const line of lines) {
    const parts = line.includes(",")
      ? line.split(",").map((x) => x.trim())
      : line.split(/\s+/).map((x) => x.trim());

    if (parts.length < 2) continue;

    const ticker = normalizeTicker(parts[0]);
    const wRaw = parts.slice(1).join("").replace("%", "");
    const w = Number(wRaw);

    if (!ticker || !Number.isFinite(w)) continue;
    rows.push({ ticker, weight: w });
  }

  const map = new Map();
  for (const r of rows) {
    map.set(r.ticker, (map.get(r.ticker) || 0) + r.weight);
  }

  return Array.from(map.entries()).map(([ticker, weight]) => ({
    ticker,
    weight,
  }));
}

function parseCorrelationCSV(csvText) {
  const lines = String(csvText || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) throw new Error("Correlation CSV looks empty.");

  const header = lines[0].split(",").map((s) => s.trim());
  const tickers = header.slice(1).map(normalizeTicker).filter(Boolean);

  const n = tickers.length;
  if (n === 0) throw new Error("No tickers found in correlation CSV header.");

  const indexOf = new Map();
  tickers.forEach((t, i) => indexOf.set(t, i));

  const mat = Array.from({ length: n }, () => Array(n).fill(NaN));

  for (let r = 1; r < lines.length; r++) {
    const parts = lines[r].split(",").map((s) => s.trim());
    if (parts.length < n + 1) continue;

    const rowTicker = normalizeTicker(parts[0]);
    const i = indexOf.get(rowTicker);
    if (i == null) continue;

    for (let j = 0; j < n; j++) {
      const v = Number(parts[j + 1]);
      mat[i][j] = Number.isFinite(v) ? v : NaN;
    }
  }

  return { tickers, indexOf, mat };
}

function parseDailyReturnsCSV(csvText) {
  const lines = String(csvText || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) throw new Error("Daily returns CSV looks empty.");

  const header = lines[0].split(",").map((s) => s.trim());
  const tickers = header.slice(1).map(normalizeTicker).filter(Boolean);

  if (!tickers.length) throw new Error("No tickers found in daily returns CSV.");

  const rows = [];

  for (let r = 1; r < lines.length; r++) {
    const parts = lines[r].split(",").map((s) => s.trim());
    if (parts.length < 2) continue;

    const date = normalizeDateString(parts[0]);
    const values = new Map();

    for (let j = 1; j < parts.length; j++) {
      const t = tickers[j - 1];
      const v = Number(parts[j]);
      values.set(t, Number.isFinite(v) ? v : NaN);
    }

    rows.push({ date, values });
  }

  return { tickers, rows };
}

async function fetchJsonFromCandidates(urls) {
  let lastError = null;

  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status} from ${url}`);
        continue;
      }

      const text = await res.text();
      const trimmed = text.trim();

      if (!trimmed) {
        lastError = new Error(`Empty response from ${url}`);
        continue;
      }

      if (
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<html") ||
        trimmed.startsWith("<")
      ) {
        lastError = new Error(`Expected JSON but received HTML from ${url}`);
        continue;
      }

      const json = JSON.parse(text);
      return { json, url };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Unable to load macro JSON.");
}

function clamp(x, lo, hi) {
  return Math.min(hi, Math.max(lo, x));
}

function clamp01(x) {
  return clamp(x, 0, 1);
}

function mean(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return NaN;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function fmtPct(x, decimals = 2) {
  if (!Number.isFinite(x)) return "—";
  return (x * 100).toFixed(decimals) + "%";
}

function fmtSignedPct(x, decimals = 2) {
  if (!Number.isFinite(x)) return "—";
  return `${x > 0 ? "+" : ""}${(x * 100).toFixed(decimals)}%`;
}

function fmtNum(x, decimals = 3) {
  if (!Number.isFinite(x)) return "—";
  return Number(x).toFixed(decimals);
}

function fmtRho(x, decimals = 3) {
  if (!Number.isFinite(x)) return "—";
  return Number(x).toFixed(decimals);
}

function fmtCurrency(x, decimals = 0) {
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(x);
}

function computeRSIFromReturns(returnSeries, period = 14) {
  if (!Array.isArray(returnSeries) || returnSeries.length < period) return NaN;

  let avgGain = 0;
  let avgLoss = 0;

  for (let i = 0; i < period; i++) {
    const r = Number(returnSeries[i]);
    if (!Number.isFinite(r)) return NaN;

    const gain = Math.max(r, 0);
    const loss = Math.max(-r, 0);
    avgGain += gain;
    avgLoss += loss;
  }

  avgGain /= period;
  avgLoss /= period;

  for (let i = period; i < returnSeries.length; i++) {
    const r = Number(returnSeries[i]);
    if (!Number.isFinite(r)) return NaN;

    const gain = Math.max(r, 0);
    const loss = Math.max(-r, 0);

    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
  }

  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

function scoreAroundOneLowerBetter(value) {
  if (!Number.isFinite(value)) return NaN;
  return clamp(50 + 50 * (1 - value), 0, 100);
}

function getSignLabel(v) {
  if (!isFiniteNumber(v)) return null;
  return v >= 0 ? "pos" : "neg";
}

function getMacroRegime(row) {
  const inf = getSignLabel(row?.inflation_score);
  const gro = getSignLabel(row?.growth_score);
  const liq = getSignLabel(row?.liquidity_score);

  if (!inf || !gro || !liq) return "—";

  const key = `${inf}_${gro}_${liq}`;

  const regimeMap = {
    pos_pos_pos: "Inflationary Growth + Easy Liquidity",
    pos_pos_neg: "Inflationary Growth",
    pos_neg_pos: "Stagflation + Easy Liquidity",
    pos_neg_neg: "Stagflation",
    neg_pos_pos: "Deflationary Growth",
    neg_pos_neg: "Deflationary Growth + Tight Liquidity",
    neg_neg_pos: "Deflationary Recession",
    neg_neg_neg: "Deflationary Recession + Tight Liquidity",
  };

  return regimeMap[key] || "—";
}

function getRegimeColors(regime) {
  if (!regime || regime === "—") {
    return {
      text: "#6b7280",
      bg: "#f3f4f6",
    };
  }

  const lower = regime.toLowerCase();

  if (lower.includes("stagflation")) {
    return { text: "#b45309", bg: "#fffbeb" };
  }
  if (lower.includes("deflationary recession")) {
    return { text: "#1d4ed8", bg: "#eff6ff" };
  }
  if (lower.includes("deflationary growth")) {
    return { text: "#047857", bg: "#ecfdf5" };
  }
  if (lower.includes("inflationary growth")) {
    return { text: "#7c3aed", bg: "#f5f3ff" };
  }

  return { text: "#374151", bg: "#f3f4f6" };
}

function getRegimeStyle(regime) {
  const c = getRegimeColors(regime);
  return {
    color: c.text,
    background: c.bg,
    fontWeight: 700,
    borderRadius: 9999,
    padding: "4px 10px",
    display: "inline-block",
  };
}

function getReturnStyle(v) {
  if (!isFiniteNumber(v)) return { color: "#6b7280" };
  if (v > 0) return { color: "#15803d", fontWeight: 700 };
  if (v < 0) return { color: "#b91c1c", fontWeight: 700 };
  return { color: "#374151", fontWeight: 600 };
}

function StatCard({ label, value, sublabel, background = "#ffffff" }) {
  return (
    <div
      style={{
        border: "1px solid #f1f5f9",
        borderRadius: 12,
        padding: 10,
        background,
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.75 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900 }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{sublabel}</div>
    </div>
  );
}

function TreemapNode(props) {
  const { x, y, width, height, name, weightPct, index } = props;

  if (width <= 0 || height <= 0) return null;

  const fill = `hsl(${(index * 47) % 360} 60% 55%)`;
  const showBigLabel = width > 70 && height > 40;
  const showSmallLabel = width > 46 && height > 24;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={8}
        ry={8}
        fill={fill}
        stroke="#ffffff"
        strokeWidth={2}
      />
      {showBigLabel && (
        <>
          <text
            x={x + 8}
            y={y + 18}
            fill="#ffffff"
            fontSize={13}
            fontWeight={800}
          >
            {name}
          </text>
          <text
            x={x + 8}
            y={y + 35}
            fill="rgba(255,255,255,0.92)"
            fontSize={12}
            fontWeight={600}
          >
            {Number.isFinite(weightPct) ? `${weightPct.toFixed(2)}%` : ""}
          </text>
        </>
      )}
      {!showBigLabel && showSmallLabel && (
        <text
          x={x + 8}
          y={y + 18}
          fill="#ffffff"
          fontSize={12}
          fontWeight={800}
        >
          {name}
        </text>
      )}
    </g>
  );
}

function RadarPerformanceTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {point?.metric || "Metric"}
      </div>
      <div>
        Avg Portfolio Return on Positive {point?.metric} Days:{" "}
        {isFiniteNumber(point?.avg_return) ? fmtPct(point.avg_return, 2) : "—"}
      </div>
      <div>
        Relative Strength Score:{" "}
        {isFiniteNumber(point?.score) ? `${point.score}/100` : "—"}
      </div>
      <div>Sample Days: {point?.count ?? "—"}</div>
    </div>
  );
}

function PieBreakdownTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {p?.name || "Type"}
      </div>
      <div>
        Portfolio weight:{" "}
        {isFiniteNumber(p?.value) ? `${p.value.toFixed(2)}%` : "—"}
      </div>
      <div>
        Holdings:{" "}
        {Array.isArray(p?.tickers) && p.tickers.length
          ? p.tickers.join(", ")
          : "—"}
      </div>
    </div>
  );
}

function PortfolioMacroStripChart({ rows, currentPortfolioValue }) {
  const chartHeight = 260;
  const plotTop = 22;
  const plotHeight = 170;
  const leftAxisWidth = 58;
  const colWidth = 28;
  const gap = 4;
  const minChartWidth = 760;

  const reconstructedValues = (() => {
    if (!isFiniteNumber(currentPortfolioValue) || rows.length === 0) {
      return rows.map(() => null);
    }

    const vals = new Array(rows.length).fill(null);
    vals[rows.length - 1] = currentPortfolioValue;

    for (let i = rows.length - 2; i >= 0; i--) {
      const nextRet = rows[i + 1]?.portfolio_return_pct;
      const nextVal = vals[i + 1];
      const nextRetDecimal = isFiniteNumber(nextRet) ? nextRet / 100 : null;

      if (
        isFiniteNumber(nextVal) &&
        isFiniteNumber(nextRetDecimal) &&
        1 + nextRetDecimal !== 0
      ) {
        vals[i] = nextVal / (1 + nextRetDecimal);
      } else {
        vals[i] = null;
      }
    }

    return vals;
  })();

  const priceValues = reconstructedValues.filter((v) => isFiniteNumber(v));
  const minPriceRaw = priceValues.length ? Math.min(...priceValues) : 0;
  const maxPriceRaw = priceValues.length ? Math.max(...priceValues) : 1;

  const padding = Math.max(
    (maxPriceRaw - minPriceRaw) * 0.12,
    maxPriceRaw * 0.01,
    0.5
  );
  const minPrice = minPriceRaw - padding;
  const maxPrice = maxPriceRaw + padding;
  const priceRange = maxPrice - minPrice || 1;

  const chartWidth = Math.max(
    minChartWidth,
    rows.length * colWidth + Math.max(0, rows.length - 1) * gap
  );

  const xForIndex = (i) => i * (colWidth + gap) + colWidth / 2;
  const yForPrice = (price) =>
    plotTop + ((maxPrice - price) / priceRange) * plotHeight;

  const points = rows.map((r, i) => {
    const price = reconstructedValues[i];
    return {
      ...r,
      reconPrice: price,
      x: xForIndex(i),
      y: isFiniteNumber(price) ? yForPrice(price) : null,
    };
  });

  const validPoints = points.filter(
    (p) => isFiniteNumber(p.x) && isFiniteNumber(p.y)
  );

  const polylinePoints = validPoints.map((p) => `${p.x},${p.y}`).join(" ");

  const uniqueRegimes = Array.from(
    new Map(rows.map((r) => [r.macro_regime, r.macro_regime])).values()
  );

  const yAxisTicks = Array.from({ length: 5 }, (_, i) => {
    const value = maxPrice - (priceRange * i) / 4;
    return {
      value,
      y: yForPrice(value),
    };
  });

  return (
    <div
      style={{
        marginTop: 14,
        border: "1px solid #f1f5f9",
        borderRadius: 12,
        padding: 12,
        background: "#fff",
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 6 }}>
        Last 30 Days Portfolio Value with Macro Regime Background
      </div>

      <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 10 }}>
        Background color shows the macro regime for each day. Line labels show
        reconstructed portfolio value using your entered portfolio value as the
        current anchor point.
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: 18, color: "#666" }}>No chart data available.</div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {uniqueRegimes.map((regime) => {
              const c = getRegimeColors(regime);
              return (
                <div
                  key={regime}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    color: "#374151",
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: c.bg,
                      border: `1px solid ${c.text}33`,
                      display: "inline-block",
                    }}
                  />
                  <span>{regime}</span>
                </div>
              );
            })}
          </div>

          <div style={{ overflowX: "auto", overflowY: "hidden" }}>
            <div
              style={{
                position: "relative",
                width: leftAxisWidth + chartWidth,
                height: chartHeight,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: leftAxisWidth,
                  height: chartHeight,
                }}
              >
                {yAxisTicks.map((tick, idx) => (
                  <div
                    key={`tick-${idx}`}
                    style={{
                      position: "absolute",
                      top: tick.y - 7,
                      right: 8,
                      fontSize: 10,
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    {formatPrice(tick.value)}
                  </div>
                ))}
              </div>

              <div
                style={{
                  position: "absolute",
                  left: leftAxisWidth,
                  top: 0,
                  width: chartWidth,
                  height: chartHeight,
                }}
              >
                {rows.map((r, i) => {
                  const c = getRegimeColors(r.macro_regime);
                  return (
                    <div
                      key={r.date}
                      style={{
                        position: "absolute",
                        left: i * (colWidth + gap),
                        top: 0,
                        width: colWidth,
                        height: plotTop + plotHeight,
                        background: c.bg,
                        border: "1px solid rgba(0,0,0,0.03)",
                        borderRadius: 4,
                      }}
                    />
                  );
                })}

                <svg
                  width={chartWidth}
                  height={chartHeight}
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "block",
                    overflow: "visible",
                  }}
                >
                  {yAxisTicks.map((tick, idx) => (
                    <line
                      key={`grid-${idx}`}
                      x1="0"
                      x2={chartWidth}
                      y1={tick.y}
                      y2={tick.y}
                      stroke="#e5e7eb"
                      strokeDasharray="3 3"
                      strokeWidth="1"
                    />
                  ))}

                  <polyline
                    fill="none"
                    stroke="#111827"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={polylinePoints}
                  />

                  {validPoints.map((p) => (
                    <circle
                      key={`pt-${p.date}`}
                      cx={p.x}
                      cy={p.y}
                      r="2.5"
                      fill="#111827"
                    />
                  ))}
                </svg>

                {validPoints.map((p) => (
                  <div
                    key={`label-${p.date}`}
                    style={{
                      position: "absolute",
                      left: p.x,
                      top: Math.max(2, p.y - 16),
                      transform: "translateX(-50%)",
                      fontSize: 10,
                      lineHeight: 1,
                      color: "#374151",
                      background: "rgba(255,255,255,0.95)",
                      padding: "2px 4px",
                      borderRadius: 4,
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                    }}
                  >
                    {formatPrice(p.reconPrice)}
                  </div>
                ))}

               {rows.map((r, i) => (
  <div
    key={`bottom-${r.date}`}
    style={{
      position: "absolute",
      left: i * (colWidth + gap),
      top: plotTop + plotHeight + 8,
      width: colWidth,
      textAlign: "center",
      fontSize: 10,
      color: "#374151",
      lineHeight: 1.15,
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 2 }}>
      {formatShortDate(r.date)}
    </div>
    <div
      style={{
        color:
          r.relative_return_pct > 0
            ? "#15803d"
            : r.relative_return_pct < 0
            ? "#b91c1c"
            : "#6b7280",
        fontWeight: 700,
      }}
    >
      {isFiniteNumber(r.relative_return_pct)
        ? `${r.relative_return_pct > 0 ? "+" : ""}${r.relative_return_pct.toFixed(1)}%`
        : "—"}
    </div>
  </div>
))}


                
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const tableThStyle = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 13,
  color: "#374151",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  verticalAlign: "top",
};

const tableTdStyle = {
  padding: "12px 14px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: 13,
  whiteSpace: "nowrap",
  verticalAlign: "top",
};

export default function PortfolioDiversification() {
  const [corrCsv, setCorrCsv] = useState("");
  const [volsJson, setVolsJson] = useState(null);
  const [expJson, setExpJson] = useState(null);
  const [dailyReturnsCsv, setDailyReturnsCsv] = useState("");
  const [divJson, setDivJson] = useState(null);
  const [sectorJson, setSectorJson] = useState(null);
  const [macroJson, setMacroJson] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [input, setInput] = useState("GOOGL 33.33\nMSFT 33.33\nAMZN 33.34");
  const [portfolioValue, setPortfolioValue] = useState("100000");

  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadAll() {
      try {
        setLoading(true);
        setErr("");

        const [
          corrRes,
          volsRes,
          expRes,
          dailyRes,
          divRes,
          sectorRes,
          macroPayload,
        ] = await Promise.all([
          fetch(CORR_CSV_URL, { cache: "no-store" }),
          fetch(VOLS_JSON_URL, { cache: "no-store" }),
          fetch(EXPECTED_RETURNS_URL, { cache: "no-store" }),
          fetch(DAILY_RETURNS_URL, { cache: "no-store" }),
          fetch(DIVIDENDS_URL, { cache: "no-store" }),
          fetch(SECTORS_URL, { cache: "no-store" }),
          fetchJsonFromCandidates(MACRO_CANDIDATE_URLS),
        ]);

        const [corrText, volsText, expText, dailyText, divText, sectorText] =
          await Promise.all([
            corrRes.text(),
            volsRes.text(),
            expRes.text(),
            dailyRes.text(),
            divRes.text(),
            sectorRes.text(),
          ]);

        if (!corrRes.ok) {
          throw new Error(
            `HTTP ${corrRes.status} fetching correlation_matrix.csv. First 120 chars: ${corrText.slice(
              0,
              120
            )}`
          );
        }
        if (corrText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of CSV for correlation_matrix.csv. First 120 chars: ${corrText.slice(
              0,
              120
            )}`
          );
        }

        if (!volsRes.ok) {
          throw new Error(
            `HTTP ${volsRes.status} fetching vols.json. First 120 chars: ${volsText.slice(
              0,
              120
            )}`
          );
        }
        if (volsText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for vols.json. First 120 chars: ${volsText.slice(
              0,
              120
            )}`
          );
        }

        if (!expRes.ok) {
          throw new Error(
            `HTTP ${expRes.status} fetching expected_returns.json. First 120 chars: ${expText.slice(
              0,
              120
            )}`
          );
        }
        if (expText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for expected_returns.json. First 120 chars: ${expText.slice(
              0,
              120
            )}`
          );
        }

        if (!dailyRes.ok) {
          throw new Error(
            `HTTP ${dailyRes.status} fetching daily_returns.csv. First 120 chars: ${dailyText.slice(
              0,
              120
            )}`
          );
        }
        if (dailyText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of CSV for daily_returns.csv. First 120 chars: ${dailyText.slice(
              0,
              120
            )}`
          );
        }

        if (!divRes.ok) {
          throw new Error(
            `HTTP ${divRes.status} fetching dividends.json. First 120 chars: ${divText.slice(
              0,
              120
            )}`
          );
        }
        if (divText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for dividends.json. First 120 chars: ${divText.slice(
              0,
              120
            )}`
          );
        }

        if (!sectorRes.ok) {
          throw new Error(
            `HTTP ${sectorRes.status} fetching sectors.json. First 120 chars: ${sectorText.slice(
              0,
              120
            )}`
          );
        }
        if (sectorText.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON for sectors.json. First 120 chars: ${sectorText.slice(
              0,
              120
            )}`
          );
        }

        const vols = JSON.parse(volsText);
        const exp = JSON.parse(expText);
        const div = JSON.parse(divText);
        const sectors = JSON.parse(sectorText);
        const macro = Array.isArray(macroPayload?.json) ? macroPayload.json : [];

        if (alive) {
          setCorrCsv(corrText);
          setVolsJson(vols);
          setExpJson(exp);
          setDailyReturnsCsv(dailyText);
          setDivJson(div);
          setSectorJson(sectors);
          setMacroJson(macro);
        }
      } catch (e) {
        if (alive) setErr(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadAll();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setAiSummary("");
    setAiError("");
  }, [input, portfolioValue]);

  const holdings = useMemo(() => parseHoldings(input), [input]);

  const corrParsed = useMemo(() => {
    if (!corrCsv) return { tickers: [], indexOf: new Map(), mat: [] };
    return parseCorrelationCSV(corrCsv);
  }, [corrCsv]);

  const dailyParsed = useMemo(() => {
    if (!dailyReturnsCsv) return { tickers: [], rows: [] };
    return parseDailyReturnsCSV(dailyReturnsCsv);
  }, [dailyReturnsCsv]);

  const volAnnualMap = useMemo(() => {
    const m = new Map();
    const obj =
      volsJson?.vol_annual && typeof volsJson.vol_annual === "object"
        ? volsJson.vol_annual
        : {};
    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const num = Number(v);
      if (t && Number.isFinite(num)) m.set(t, num);
    }
    return m;
  }, [volsJson]);

  const expAnnualMap = useMemo(() => {
    const m = new Map();
    const obj =
      expJson?.expected_return_annual_pct &&
      typeof expJson.expected_return_annual_pct === "object"
        ? expJson.expected_return_annual_pct
        : {};
    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const num = Number(v);
      if (t && Number.isFinite(num)) m.set(t, num);
    }
    return m;
  }, [expJson]);

  const dividendYieldMap = useMemo(() => {
    const m = new Map();
    const obj =
      divJson?.dividend_yield_pct &&
      typeof divJson.dividend_yield_pct === "object"
        ? divJson.dividend_yield_pct
        : {};

    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const num = Number(v);
      if (t && Number.isFinite(num)) m.set(t, num);
    }
    return m;
  }, [divJson]);

  const sectorMap = useMemo(() => {
    const m = new Map();
    const obj =
      sectorJson?.sector && typeof sectorJson.sector === "object"
        ? sectorJson.sector
        : {};

    for (const [k, v] of Object.entries(obj)) {
      const t = normalizeTicker(k);
      const sector = String(v || "").trim() || "Unknown";
      if (t) m.set(t, sector);
    }
    return m;
  }, [sectorJson]);

  const portfolioValueNum = useMemo(() => {
    const cleaned = String(portfolioValue || "").replace(/[$,]/g, "").trim();
    const num = Number(cleaned);
    return Number.isFinite(num) && num >= 0 ? num : NaN;
  }, [portfolioValue]);

  const result = useMemo(() => {
    const { indexOf, mat } = corrParsed;

    if (!holdings.length) {
      return { ok: false, reason: "Enter at least 2 tickers + weights." };
    }
    if (!mat.length) {
      return { ok: false, reason: "Correlation matrix not loaded yet." };
    }
    if (!volAnnualMap.size) {
      return { ok: false, reason: "Volatility map not loaded yet." };
    }
    if (!expAnnualMap.size) {
      return { ok: false, reason: "Expected return map not loaded yet." };
    }
    if (!dailyParsed.rows.length) {
      return { ok: false, reason: "Daily returns matrix not loaded yet." };
    }
    if (!dailyParsed.tickers.includes("SPY")) {
      return {
        ok: false,
        reason:
          "SPY is missing from daily_returns.csv. Add SPY to the backend universe.",
      };
    }

    const present = [];
    const missing = [];
    const missingReasons = [];

    for (const h of holdings) {
      const t = normalizeTicker(h.ticker);
      const i = indexOf.get(t);
      const volA = volAnnualMap.get(t);
      const muA = expAnnualMap.get(t);
      const inDaily = dailyParsed.tickers.includes(t);

      const miss = [];
      if (i == null) miss.push("corr");
      if (volA == null) miss.push("vol");
      if (muA == null) miss.push("exp");
      if (!inDaily) miss.push("daily");

      if (miss.length) {
        missing.push(t);
        missingReasons.push({ ticker: t, missing: miss });
      } else {
        present.push({ ticker: t, weight: h.weight, idx: i, volA, muA });
      }
    }

    if (present.length < 2) {
      return {
        ok: false,
        reason:
          "Need at least 2 tickers that exist in correlation matrix, vols.json, expected_returns.json, and daily_returns.csv.",
        missing,
        missingReasons,
      };
    }

    const sumW = present.reduce((a, b) => a + b.weight, 0);
    if (!Number.isFinite(sumW) || sumW <= 0) {
      return {
        ok: false,
        reason: "Weights must sum to a positive number.",
        missing,
      };
    }

    const n = present.length;
    const w = present.map((p) => p.weight / sumW);

    let num = 0;
    let den = 0;
    let nanPairs = 0;
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const rho = mat[present[a].idx][present[b].idx];
        const wij = w[a] * w[b];
        den += wij;
        if (Number.isFinite(rho)) num += wij * rho;
        else nanPairs += 1;
      }
    }
    const rhoBar = den > 0 ? num / den : NaN;

    let qCorr = 0;
    for (let a = 0; a < n; a++) {
      for (let b = 0; b < n; b++) {
        const rho = mat[present[a].idx][present[b].idx];
        const safe = Number.isFinite(rho) ? rho : a === b ? 1 : 0;
        qCorr += w[a] * w[b] * safe;
      }
    }

    const neffCorr = qCorr > 0 ? 1 / qCorr : NaN;

    const diversificationScore =
      Number.isFinite(neffCorr) && n > 1
        ? clamp(((neffCorr - 1) / (n - 1)) * 100, 0, 100)
        : n === 1
        ? 0
        : NaN;

    const volDec = present.map((p) => p.volA / 100.0);

    let varP = 0;
    for (let a = 0; a < n; a++) {
      for (let b = 0; b < n; b++) {
        const rhoRaw = mat[present[a].idx][present[b].idx];
        const rho = Number.isFinite(rhoRaw) ? rhoRaw : a === b ? 1 : 0;
        varP += w[a] * w[b] * rho * volDec[a] * volDec[b];
      }
    }
    const volP = varP > 0 ? Math.sqrt(varP) : NaN;
    const dailyMove = Number.isFinite(volP)
      ? volP / Math.sqrt(TRADING_DAYS)
      : NaN;

    const wAvgVol = volDec.reduce((acc, s, i) => acc + w[i] * s, 0);
    const divRatio = Number.isFinite(volP) && volP > 0 ? wAvgVol / volP : NaN;

    const muDec = present.map((p) => p.muA / 100.0);
    const portReturn = muDec.reduce((acc, mu, i) => acc + w[i] * mu, 0);

    const sharpe =
      Number.isFinite(volP) && volP > 0
        ? (portReturn - RISK_FREE_RATE) / volP
        : NaN;

    const m = new Array(n).fill(0);
    for (let a = 0; a < n; a++) {
      let sum = 0;
      for (let b = 0; b < n; b++) {
        const rhoRaw = mat[present[a].idx][present[b].idx];
        const rho = Number.isFinite(rhoRaw) ? rhoRaw : a === b ? 1 : 0;
        const cov = rho * volDec[a] * volDec[b];
        sum += w[b] * cov;
      }
      m[a] = sum;
    }

    const varContrib = m.map((mi, i) => w[i] * mi);
    const pctVarContrib =
      varP > 0 ? varContrib.map((x) => x / varP) : varContrib.map(() => NaN);

    const riskTable = present
      .map((p, i) => ({
        ticker: p.ticker,
        weightPct: w[i] * 100,
        expAnnualPct: p.muA,
        volAnnualPct: p.volA,
        pctVariance: pctVarContrib[i],
        dividendYieldPct: dividendYieldMap.get(p.ticker) ?? NaN,
        sector: sectorMap.get(p.ticker) || "Unknown",
      }))
      .sort((a, b) => (b.pctVariance ?? -1) - (a.pctVariance ?? -1));

    const normalizedHoldings = present
      .map((p, i) => ({ ticker: p.ticker, weightPct: w[i] * 100 }))
      .sort((a, b) => b.weightPct - a.weightPct);

    const sectorWeightsMap = new Map();
    for (let i = 0; i < present.length; i++) {
      const ticker = present[i].ticker;
      const sector = sectorMap.get(ticker) || "Unknown";
      const prev = sectorWeightsMap.get(sector) || 0;
      sectorWeightsMap.set(sector, prev + w[i]);
    }

    const sectorWeights = Array.from(sectorWeightsMap.entries())
      .map(([sector, weight]) => ({
        sector,
        weight,
        weightPct: weight * 100,
      }))
      .sort((a, b) => b.weight - a.weight);

    const topSector = sectorWeights.length ? sectorWeights[0] : null;
    const sectorHHI = sectorWeights.reduce(
      (acc, s) => acc + s.weight * s.weight,
      0
    );
    const effectiveSectors = sectorHHI > 0 ? 1 / sectorHHI : NaN;

    const DEFENSIVE_SECTORS = new Set([
      "Healthcare",
      "Consumer Defensive",
      "Consumer Staples",
      "Utilities",
    ]);

    const defensiveSectorWeight = sectorWeights.reduce((acc, s) => {
      return acc + (DEFENSIVE_SECTORS.has(s.sector) ? s.weight : 0);
    }, 0);

    const portfolioSeries = [];

    for (const row of dailyParsed.rows) {
      let rp = 0;
      let valid = true;

      for (let i = 0; i < present.length; i++) {
        const t = present[i].ticker;
        const riPct = row.values.get(t);

        if (!Number.isFinite(riPct)) {
          valid = false;
          break;
        }

        rp += w[i] * (riPct / 100.0);
      }

      const spyPct = row.values.get("SPY");

      if (!valid || !Number.isFinite(spyPct)) continue;

      portfolioSeries.push({
        date: normalizeDateString(row.date),
        rp,
        spy: spyPct / 100.0,
      });
    }

    if (portfolioSeries.length < 20) {
      return {
        ok: false,
        reason:
          "Not enough overlapping daily return history across your holdings and SPY to compute defensive metrics / RSI.",
        missing,
        missingReasons,
      };
    }

    const rpArr = portfolioSeries.map((x) => x.rp);
    const spyArr = portfolioSeries.map((x) => x.spy);

    const meanRp = mean(rpArr);
    const meanSpy = mean(spyArr);

    let covPS = 0;
    let varSpy = 0;
    let varRp = 0;

    for (let i = 0; i < portfolioSeries.length; i++) {
      const a = rpArr[i] - meanRp;
      const b = spyArr[i] - meanSpy;
      covPS += a * b;
      varRp += a * a;
      varSpy += b * b;
    }

    if (portfolioSeries.length > 1) {
      covPS /= portfolioSeries.length - 1;
      varRp /= portfolioSeries.length - 1;
      varSpy /= portfolioSeries.length - 1;
    } else {
      covPS = NaN;
      varRp = NaN;
      varSpy = NaN;
    }

    const betaSpy =
      Number.isFinite(covPS) && Number.isFinite(varSpy) && varSpy > 0
        ? covPS / varSpy
        : NaN;

    const corrSpy =
      Number.isFinite(covPS) &&
      Number.isFinite(varRp) &&
      Number.isFinite(varSpy) &&
      varRp > 0 &&
      varSpy > 0
        ? covPS / Math.sqrt(varRp * varSpy)
        : NaN;

    const downDays = portfolioSeries.filter((x) => x.spy < 0);

    const avgPortDown = downDays.length
      ? downDays.reduce((a, x) => a + x.rp, 0) / downDays.length
      : NaN;

    const avgSpyDown = downDays.length
      ? downDays.reduce((a, x) => a + x.spy, 0) / downDays.length
      : NaN;

    const downsideCapture =
      Number.isFinite(avgPortDown) &&
      Number.isFinite(avgSpyDown) &&
      avgSpyDown !== 0
        ? avgPortDown / avgSpyDown
        : NaN;

    const upWhenSpyDownRate = downDays.length
      ? downDays.filter((x) => x.rp > 0).length / downDays.length
      : NaN;

    const portDailyVol = Number.isFinite(varRp) ? Math.sqrt(varRp) : NaN;
    const spyDailyVol = Number.isFinite(varSpy) ? Math.sqrt(varSpy) : NaN;

    const relativeVolToSpy =
      Number.isFinite(portDailyVol) &&
      Number.isFinite(spyDailyVol) &&
      spyDailyVol > 0
        ? portDailyVol / spyDailyVol
        : NaN;

    const portfolioRSI14 = computeRSIFromReturns(rpArr, 14);

    const betaScore = Number.isFinite(betaSpy) ? clamp01(1 - betaSpy) : NaN;
    const downsideScore = Number.isFinite(downsideCapture)
      ? clamp01(1 - downsideCapture)
      : NaN;
    const hitRateScore = Number.isFinite(upWhenSpyDownRate)
      ? clamp01(upWhenSpyDownRate / 0.5)
      : NaN;
    const relVolScore = Number.isFinite(relativeVolToSpy)
      ? clamp01(1 - Math.max(0, relativeVolToSpy - 0.5) / 1.0)
      : NaN;

    const defensiveScoreLegacy =
      [betaScore, downsideScore, hitRateScore, relVolScore].every(
        Number.isFinite
      )
        ? 100 *
          (0.3 * betaScore +
            0.35 * downsideScore +
            0.2 * hitRateScore +
            0.15 * relVolScore)
        : NaN;

    const volatilityScore50 = scoreAroundOneLowerBetter(relativeVolToSpy);
    const defensivenessScore50 = scoreAroundOneLowerBetter(downsideCapture);
    const defensiveScore = defensivenessScore50;

    const portfolioDividendYield =
      present.reduce((acc, p, i) => {
        const y = dividendYieldMap.get(p.ticker);
        if (!Number.isFinite(y)) return acc;
        return acc + w[i] * (y / 100.0);
      }, 0) || 0;

    const annualDividendIncome = Number.isFinite(portfolioValueNum)
      ? portfolioValueNum * portfolioDividendYield
      : NaN;

    const treemapData = normalizedHoldings.map((h) => ({
      name: h.ticker,
      size: h.weightPct,
      weightPct: h.weightPct,
    }));

    const portfolioReturnByDate = new Map(
      portfolioSeries.map((x) => [normalizeDateString(x.date), x])
    );

    const portfolioMacroRows = Array.isArray(macroJson)
      ? macroJson
          .map((m) => {
            const date = normalizeDateString(m?.date);
            const portRow = portfolioReturnByDate.get(date);
            if (!portRow) return null;

            return {
              date,
              portfolio_return: portRow.rp,
              spy_return: portRow.spy,
              relative_return:
                isFiniteNumber(portRow.rp) && isFiniteNumber(portRow.spy)
                  ? portRow.rp - portRow.spy
                  : null,
              inflation_score: Number.isFinite(Number(m?.inflation_score))
                ? Number(m.inflation_score)
                : null,
              growth_score: Number.isFinite(Number(m?.growth_score))
                ? Number(m.growth_score)
                : null,
              liquidity_score: Number.isFinite(Number(m?.liquidity_score))
                ? Number(m.liquidity_score)
                : null,
            };
          })
          .filter(Boolean)
          .map((r) => ({
            ...r,
            macro_regime: getMacroRegime(r),
          }))
      : [];

    const portfolioMacroSummaryRows = (() => {
      const groups = new Map();

      for (const row of portfolioMacroRows) {
        const key = row.macro_regime || "—";
        if (!groups.has(key)) {
          groups.set(key, {
            macro_regime: key,
            count: 0,
            sum_return: 0,
            sum_relative: 0,
            num_up: 0,
            count_return: 0,
            count_relative: 0,
          });
        }

        const g = groups.get(key);
        g.count += 1;

        if (isFiniteNumber(row.portfolio_return)) {
          g.sum_return += row.portfolio_return;
          g.count_return += 1;
          if (row.portfolio_return > 0) g.num_up += 1;
        }

        if (isFiniteNumber(row.relative_return)) {
          g.sum_relative += row.relative_return;
          g.count_relative += 1;
        }
      }

      return [...groups.values()]
        .map((g) => ({
          macro_regime: g.macro_regime,
          total_days: g.count,
          win_rate: g.count_return > 0 ? g.num_up / g.count_return : null,
          avg_daily_return:
            g.count_return > 0 ? g.sum_return / g.count_return : null,
          avg_return_vs_spy:
            g.count_relative > 0 ? g.sum_relative / g.count_relative : null,
        }))
        .sort((a, b) => {
          const aVal = isFiniteNumber(a.avg_daily_return)
            ? a.avg_daily_return
            : -999;
          const bVal = isFiniteNumber(b.avg_daily_return)
            ? b.avg_daily_return
            : -999;
          return bVal - aVal;
        });
    })();

    const portfolioRadarData = (() => {
      const defs = [
        { key: "inflation_score", metric: "Inflation" },
        { key: "growth_score", metric: "Growth" },
        { key: "liquidity_score", metric: "Liquidity" },
      ];

      const raw = defs.map(({ key, metric }) => {
        const matched = portfolioMacroRows.filter(
          (r) =>
            isFiniteNumber(r?.[key]) &&
            r[key] > 0 &&
            isFiniteNumber(r.portfolio_return)
        );

        const avgReturn =
          matched.length > 0
            ? matched.reduce((sum, r) => sum + r.portfolio_return, 0) /
              matched.length
            : null;

        return {
          metric,
          avg_return: avgReturn,
          count: matched.length,
        };
      });

      const vals = raw
        .map((r) => r.avg_return)
        .filter((v) => isFiniteNumber(v));

      const min = vals.length ? Math.min(...vals) : 0;
      const max = vals.length ? Math.max(...vals) : 0;

      return raw.map((r) => {
        let score = 50;

        if (isFiniteNumber(r.avg_return) && max !== min) {
          score = 20 + ((r.avg_return - min) / (max - min)) * 80;
        } else if (isFiniteNumber(r.avg_return) && max === min) {
          score = 60;
        }

        return {
          ...r,
          score: Math.round(score),
          fullMark: 100,
        };
      });
    })();

    const bestPortfolioMacroType = (() => {
      if (!portfolioMacroSummaryRows.length) return "—";
      const valid = portfolioMacroSummaryRows.filter((r) =>
        isFiniteNumber(r.avg_daily_return)
      );
      if (!valid.length) return "—";
      return valid[0]?.macro_regime || "—";
    })();

    const sortedPortfolioMacroRows = [...portfolioMacroRows].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    const last30PortfolioRows = sortedPortfolioMacroRows.slice(-30).map((r) => ({
      date: r.date,
      macro_regime: r.macro_regime,
      portfolio_return_pct: isFiniteNumber(r.portfolio_return)
        ? r.portfolio_return * 100
        : null,
      relative_return_pct: isFiniteNumber(r.relative_return)
        ? r.relative_return * 100
        : null,
    }));

    const dailyRowByDate = new Map(
      dailyParsed.rows.map((row) => [normalizeDateString(row.date), row.values])
    );

    const holdingMacroTypes = present.map((p, i) => {
      const rows = Array.isArray(macroJson)
        ? macroJson
            .map((m) => {
              const date = normalizeDateString(m?.date);
              const values = dailyRowByDate.get(date);
              if (!values) return null;

              const stockPct = values.get(p.ticker);
              const spyPct = values.get("SPY");

              if (!Number.isFinite(stockPct) || !Number.isFinite(spyPct)) {
                return null;
              }

              const stockReturn = stockPct / 100.0;
              const spyReturn = spyPct / 100.0;

              return {
                date,
                stock_return: stockReturn,
                spy_return: spyReturn,
                relative_return: stockReturn - spyReturn,
                inflation_score: Number.isFinite(Number(m?.inflation_score))
                  ? Number(m.inflation_score)
                  : null,
                growth_score: Number.isFinite(Number(m?.growth_score))
                  ? Number(m.growth_score)
                  : null,
                liquidity_score: Number.isFinite(Number(m?.liquidity_score))
                  ? Number(m.liquidity_score)
                  : null,
              };
            })
            .filter(Boolean)
            .map((r) => ({
              ...r,
              macro_regime: getMacroRegime(r),
            }))
        : [];

      const groups = new Map();

      for (const row of rows) {
        const key = row.macro_regime || "—";
        if (!groups.has(key)) {
          groups.set(key, {
            macro_regime: key,
            count_return: 0,
            sum_return: 0,
          });
        }
        const g = groups.get(key);
        if (isFiniteNumber(row.stock_return)) {
          g.sum_return += row.stock_return;
          g.count_return += 1;
        }
      }

      const summary = [...groups.values()]
        .map((g) => ({
          macro_regime: g.macro_regime,
          avg_daily_return:
            g.count_return > 0 ? g.sum_return / g.count_return : null,
        }))
        .filter((g) => isFiniteNumber(g.avg_daily_return))
        .sort((a, b) => b.avg_daily_return - a.avg_daily_return);

      const bestMacroType = summary.length
        ? summary[0].macro_regime
        : "Unclassified";

      return {
        ticker: p.ticker,
        weight: w[i],
        weightPct: w[i] * 100,
        bestMacroType,
      };
    });

    const stockTypeWeightsMap = new Map();

    for (const h of holdingMacroTypes) {
      const key = h.bestMacroType || "Unclassified";
      if (!stockTypeWeightsMap.has(key)) {
        stockTypeWeightsMap.set(key, {
          name: key,
          value: 0,
          tickers: [],
        });
      }
      const entry = stockTypeWeightsMap.get(key);
      entry.value += h.weightPct;
      entry.tickers.push(h.ticker);
    }

    const stockTypePieData = [...stockTypeWeightsMap.values()]
      .map((x) => ({
        ...x,
        value: Number(x.value.toFixed(4)),
      }))
      .sort((a, b) => b.value - a.value);

    return {
      ok: true,
      missing,
      missingReasons,
      usedCount: n,
      overlapDays: portfolioSeries.length,
      spyDownDays: downDays.length,

      rhoBar,
      neffCorr,
      diversificationScore,
      nanPairs,

      volP,
      dailyMove,
      wAvgVol,
      divRatio,
      portReturn,
      sharpe,

      betaSpy,
      corrSpy,
      downsideCapture,
      upWhenSpyDownRate,
      relativeVolToSpy,
      defensiveScore,
      defensiveScoreLegacy,
      defensiveScore50: defensivenessScore50,
      volatilityScore50,
      portfolioRSI14,

      portfolioDividendYield,
      annualDividendIncome,

      sectorWeights,
      topSector,
      sectorHHI,
      effectiveSectors,
      defensiveSectorWeight,

      normalizedHoldings,
      riskTable,
      treemapData,

      portfolioMacroRows,
      portfolioMacroSummaryRows,
      portfolioRadarData,
      bestPortfolioMacroType,
      sortedPortfolioMacroRows,
      last30PortfolioRows,

      holdingMacroTypes,
      stockTypePieData,
    };
  }, [
    holdings,
    corrParsed,
    volAnnualMap,
    expAnnualMap,
    dailyParsed,
    dividendYieldMap,
    sectorMap,
    portfolioValueNum,
    macroJson,
  ]);

  async function handleGenerateSummary() {
    if (!result?.ok || aiLoading) return;

    try {
      setAiLoading(true);
      setAiError("");
      const text = await generatePortfolioSummary(result);
      setAiSummary(text || "");
      if (!text) {
        setAiError("The model returned an empty summary.");
      }
    } catch (e) {
      setAiError(e?.message || String(e));
    } finally {
      setAiLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        Loading correlation + vol + return + daily + dividend + sector + macro
        data…
      </div>
    );
  }

  if (err) {
    return <div style={{ padding: 16, color: "crimson" }}>Error: {err}</div>;
  }

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, marginBottom: 10 }}>
        Portfolio Diversification (Sharpe + Defense + RSI + Dividends + Sectors)
      </h1>

      <div style={{ opacity: 0.8, marginBottom: 14 }}>
        Includes corr-only metrics + covariance (corr × vol × vol) + expected
        return + Sharpe + SPY downside defense + portfolio RSI + dividend yield
        + sector distribution + portfolio-level macro regime analysis.
        {volsJson?.as_of ? ` • vols as of ${volsJson.as_of}` : ""}
        {expJson?.as_of ? ` • returns as of ${expJson.as_of}` : ""}
        {divJson?.as_of ? ` • dividends as of ${divJson.as_of}` : ""}
        {sectorJson?.as_of ? ` • sectors as of ${sectorJson.as_of}` : ""}
        {` • Rf = ${(RISK_FREE_RATE * 100).toFixed(2)}%`}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 420px) 1fr",
          gap: 14,
          alignItems: "start",
        }}
      >
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Holdings</div>
          <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 8 }}>
            One per line:{" "}
            <span style={{ fontFamily: "monospace" }}>TICKER weight</span>{" "}
            (weights can be %, don’t need to sum to 100)
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={10}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              outline: "none",
              resize: "vertical",
            }}
          />

          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>
              Portfolio value
            </div>
            <input
              value={portfolioValue}
              onChange={(e) => setPortfolioValue(e.target.value)}
              placeholder="100000"
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                fontSize: 14,
                outline: "none",
              }}
            />
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
              Used to estimate annual dividend income.
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
            Sources:
            <div style={{ fontFamily: "monospace" }}>
              corr: majorah-data/data/correlation_matrix.csv
            </div>
            <div style={{ fontFamily: "monospace" }}>
              vols: majorah-data/public/data/vols.json
            </div>
            <div style={{ fontFamily: "monospace" }}>
              exp: majorah-data/public/data/expected_returns.json
            </div>
            <div style={{ fontFamily: "monospace" }}>
              daily: majorah-data/data/daily_returns.csv
            </div>
            <div style={{ fontFamily: "monospace" }}>
              div: majorah-data/public/data/dividends.json
            </div>
            <div style={{ fontFamily: "monospace" }}>
              sectors: majorah-data/public/data/sectors.json
            </div>
            <div style={{ fontFamily: "monospace" }}>
              macro: majorah-data/public/data/macro_dimension_scores.json
            </div>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Results</div>

          {!result.ok ? (
            <div style={{ color: "crimson" }}>{result.reason}</div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: 12,
                }}
              >
                <button
                  onClick={handleGenerateSummary}
                  disabled={aiLoading}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    background: aiLoading ? "#f3f4f6" : "#111827",
                    color: aiLoading ? "#6b7280" : "#ffffff",
                    cursor: aiLoading ? "default" : "pointer",
                    fontWeight: 700,
                  }}
                >
                  {aiLoading ? "Generating..." : "Generate AI Summary"}
                </button>

                {aiError && (
                  <span style={{ color: "crimson", fontSize: 13 }}>
                    {aiError}
                  </span>
                )}
              </div>

              {aiSummary && (
                <div
                  style={{
                    marginBottom: 14,
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    background: "#fafafa",
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>
                    AI Portfolio Summary
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                    {aiSummary}
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <StatCard
                  label="Number of holdings"
                  value={String(result.usedCount)}
                  sublabel="Tickers used in final calculation"
                  background="#f8fafc"
                />

                <StatCard
                  label="Volatility score"
                  value={
                    Number.isFinite(result.volatilityScore50)
                      ? result.volatilityScore50.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • 50 = Vol vs SPY of 1.00"
                  background="#f8fafc"
                />

                <StatCard
                  label="Defensiveness score"
                  value={
                    Number.isFinite(result.defensiveScore50)
                      ? result.defensiveScore50.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • 50 = Downside Capture of 1.00"
                  background="#f8fafc"
                />

                <StatCard
                  label="Diversification score"
                  value={
                    Number.isFinite(result.diversificationScore)
                      ? result.diversificationScore.toFixed(0)
                      : "—"
                  }
                  sublabel="0–100 • based on effective independent bets"
                  background="#f8fafc"
                />
              </div>

              <div
                style={{
                  marginTop: 10,
                  border: "1px solid #f1f5f9",
                  borderRadius: 12,
                  padding: 12,
                  background: "#ffffff",
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 6 }}>
                  Holdings treemap
                </div>
                <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 10 }}>
                  Block area = normalized portfolio weight
                </div>

                <div style={{ width: "100%", height: 250 }}>
                  <ResponsiveContainer>
                    <Treemap
                      data={result.treemapData}
                      dataKey="size"
                      nameKey="name"
                      stroke="#ffffff"
                      content={<TreemapNode />}
                    >
                      <Tooltip
                        formatter={(value, _name, entry) => [
                          `${Number(value).toFixed(2)}%`,
                          entry?.payload?.name || "Weight",
                        ]}
                      />
                    </Treemap>
                  </ResponsiveContainer>
                </div>

                <PortfolioMacroStripChart
                  rows={result.last30PortfolioRows}
                  currentPortfolioValue={
                    Number.isFinite(portfolioValueNum) && portfolioValueNum > 0
                      ? portfolioValueNum
                      : 100000
                  }
                />
              </div>

              <div
                style={{
                  marginTop: 14,
                  border: "1px solid #eee",
                  borderRadius: 14,
                  background: "#fff",
                  padding: 12,
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 10 }}>
                  Portfolio Macro Profile
                </div>

                <div
                  style={{
                    marginBottom: 12,
                    display: "inline-block",
                    padding: "6px 10px",
                    borderRadius: 9999,
                    background: "#f3f4f6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Portfolio Type: {result.bestPortfolioMacroType}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(340px, 1fr) minmax(420px, 1fr)",
                    gap: 16,
                    alignItems: "start",
                  }}
                >
                  <div>
                    {result.portfolioRadarData.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No portfolio macro radar data available.
                      </div>
                    ) : (
                      <div style={{ width: "100%", height: 340 }}>
                        <ResponsiveContainer>
                          <RadarChart data={result.portfolioRadarData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="metric" />
                            <PolarRadiusAxis
                              domain={[0, 100]}
                              tick={false}
                              axisLine={false}
                            />
                            <Radar
                              name="Portfolio Macro Performance"
                              dataKey="score"
                              stroke="#7c3aed"
                              fill="#7c3aed"
                              fillOpacity={0.28}
                            />
                            <Legend />
                            <Tooltip content={<RadarPerformanceTooltip />} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      minHeight: 340,
                    }}
                  >
                    <div
                      style={{
                        padding: 10,
                        background: "#fafafa",
                        borderBottom: "1px solid #eee",
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      Portfolio Macro Comparison Table
                    </div>

                    {result.portfolioMacroSummaryRows.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No portfolio macro comparison data available.
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto", maxHeight: 340 }}>
                        <table
                          style={{
                            width: "100%",
                            minWidth: 760,
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead
                            style={{
                              background: "#f9fafb",
                              position: "sticky",
                              top: 0,
                            }}
                          >
                            <tr>
                              <th style={tableThStyle}>Macro Environment</th>
                              <th style={tableThStyle}>Win Rate</th>
                              <th style={tableThStyle}>Avg Daily Return</th>
                              <th style={tableThStyle}>Avg Return vs SPY</th>
                              <th style={tableThStyle}>Total Days</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.portfolioMacroSummaryRows.map((r) => (
                              <tr key={r.macro_regime}>
                                <td style={tableTdStyle}>
                                  <span style={getRegimeStyle(r.macro_regime)}>
                                    {r.macro_regime}
                                  </span>
                                </td>
                                <td style={tableTdStyle}>
                                  {isFiniteNumber(r.win_rate)
                                    ? `${(r.win_rate * 100).toFixed(1)}%`
                                    : "—"}
                                </td>
                                <td
                                  style={{
                                    ...tableTdStyle,
                                    ...getReturnStyle(r.avg_daily_return),
                                  }}
                                >
                                  {fmtSignedPct(r.avg_daily_return, 2)}
                                </td>
                                <td
                                  style={{
                                    ...tableTdStyle,
                                    ...getReturnStyle(r.avg_return_vs_spy),
                                  }}
                                >
                                  {fmtSignedPct(r.avg_return_vs_spy, 2)}
                                </td>
                                <td style={tableTdStyle}>{r.total_days}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    display: "grid",
                    gridTemplateColumns: "minmax(340px, 1fr) minmax(420px, 1fr)",
                    gap: 16,
                    alignItems: "start",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      minHeight: 340,
                    }}
                  >
                    <div
                      style={{
                        padding: 10,
                        background: "#fafafa",
                        borderBottom: "1px solid #eee",
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      Portfolio Stock-Type Composition
                    </div>

                    {result.stockTypePieData.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No stock-type breakdown available.
                      </div>
                    ) : (
                      <div style={{ width: "100%", height: 340 }}>
                        <ResponsiveContainer>
                          <PieChart width={320} height={320}>
                            <Pie
                              data={result.stockTypePieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={78}
                              innerRadius={40}
                              paddingAngle={2}
                              label={({ cx, cy, midAngle, outerRadius, name, value }) => {
                                const RADIAN = Math.PI / 180;
                                const radius = outerRadius + 28;
                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                const percentText = `${Number(value).toFixed(1)}%`;

                                const words = String(name || "").split(" ");
                                const lines = [];
                                let currentLine = "";

                                for (const word of words) {
                                  const testLine = currentLine
                                    ? `${currentLine} ${word}`
                                    : word;
                                  if (testLine.length <= 16) {
                                    currentLine = testLine;
                                  } else {
                                    if (currentLine) lines.push(currentLine);
                                    currentLine = word;
                                  }
                                }

                                if (currentLine) lines.push(currentLine);
                                lines.push(percentText);

                                const lineHeight = 13;
                                const startDy = -((lines.length - 1) * lineHeight) / 2;

                                return (
                                  <text
                                    x={x}
                                    y={y}
                                    fill="#000"
                                    textAnchor={x > cx ? "start" : "end"}
                                    dominantBaseline="central"
                                    fontSize={11}
                                    fontWeight={600}
                                  >
                                    {lines.map((line, i) => (
                                      <tspan
                                        key={`${name}-${i}`}
                                        x={x}
                                        dy={i === 0 ? startDy : lineHeight}
                                      >
                                        {line}
                                      </tspan>
                                    ))}
                                  </text>
                                );
                              }}
                              labelLine={false}
                            >
                              {result.stockTypePieData.map((entry) => {
                                const c = getRegimeColors(entry.name);
                                return (
                                  <Cell
                                    key={`pie-${entry.name}`}
                                    fill={c.bg}
                                    stroke={c.text}
                                    strokeWidth={1.5}
                                  />
                                );
                              })}
                            </Pie>

                            <Tooltip
                              contentStyle={{ color: "#000" }}
                              itemStyle={{ color: "#000" }}
                              labelStyle={{ color: "#000" }}
                              content={<PieBreakdownTooltip />}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      border: "1px solid #f3f4f6",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      minHeight: 340,
                    }}
                  >
                    <div
                      style={{
                        padding: 10,
                        background: "#fafafa",
                        borderBottom: "1px solid #eee",
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      Holding Stock Types
                    </div>

                    {result.holdingMacroTypes.length === 0 ? (
                      <div style={{ padding: 18, color: "#666" }}>
                        No holding type data available.
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto", maxHeight: 340 }}>
                        <table
                          style={{
                            width: "100%",
                            minWidth: 640,
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead
                            style={{
                              background: "#f9fafb",
                              position: "sticky",
                              top: 0,
                            }}
                          >
                            <tr>
                              <th style={tableThStyle}>Ticker</th>
                              <th style={tableThStyle}>Weight</th>
                              <th style={tableThStyle}>Stock Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.holdingMacroTypes
                              .slice()
                              .sort((a, b) => b.weightPct - a.weightPct)
                              .map((r) => (
                                <tr key={r.ticker}>
                                  <td style={tableTdStyle}>{r.ticker}</td>
                                  <td style={tableTdStyle}>
                                    {r.weightPct.toFixed(2)}%
                                  </td>
                                  <td style={tableTdStyle}>
                                    <span style={getRegimeStyle(r.bestMacroType)}>
                                      {r.bestMacroType}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <StatCard
                  label="Portfolio vol (annual)"
                  value={fmtPct(result.volP, 2)}
                  sublabel="σₚ"
                  background="#fafafa"
                />

                <StatCard
                  label="Expected daily move"
                  value={fmtPct(result.dailyMove, 2)}
                  sublabel="σₚ / √252"
                  background="#fafafa"
                />

                <StatCard
                  label="Expected return (annual)"
                  value={fmtPct(result.portReturn, 2)}
                  sublabel="E[Rₚ]"
                  background="#fafafa"
                />

                <StatCard
                  label="Sharpe ratio"
                  value={fmtNum(result.sharpe, 3)}
                  sublabel="(E[Rₚ] − Rf) / σₚ"
                  background="#fafafa"
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <StatCard
                  label="Dividend yield"
                  value={fmtPct(result.portfolioDividendYield, 2)}
                  sublabel="Weighted portfolio yield"
                />

                <StatCard
                  label="Annual dividend income"
                  value={fmtCurrency(result.annualDividendIncome, 0)}
                  sublabel="Based on portfolio value"
                />

                <StatCard
                  label="Defensive score"
                  value={
                    Number.isFinite(result.defensiveScore)
                      ? `${result.defensiveScore.toFixed(0)} / 100`
                      : "—"
                  }
                  sublabel="Mapped directly from downside capture"
                />

                <StatCard
                  label="Portfolio RSI (14)"
                  value={fmtNum(result.portfolioRSI14, 1)}
                  sublabel="Synthetic portfolio RSI"
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <StatCard
                  label="Top sector"
                  value={result.topSector ? result.topSector.sector : "—"}
                  sublabel={
                    result.topSector
                      ? `${result.topSector.weightPct.toFixed(1)}%`
                      : "—"
                  }
                />

                <StatCard
                  label="Effective sectors"
                  value={fmtNum(result.effectiveSectors, 2)}
                  sublabel="1 / sector HHI"
                />

                <StatCard
                  label="Defensive sector weight"
                  value={fmtPct(result.defensiveSectorWeight, 1)}
                  sublabel="Healthcare + staples + utilities"
                />

                <StatCard
                  label="Sector concentration"
                  value={fmtNum(result.sectorHHI, 3)}
                  sublabel="Higher = more concentrated"
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <StatCard
                  label="Beta vs SPY"
                  value={fmtNum(result.betaSpy, 3)}
                  sublabel="Lower = more defensive"
                />

                <StatCard
                  label="Downside capture"
                  value={fmtNum(result.downsideCapture, 3)}
                  sublabel="This drives Defensiveness score"
                />

                <StatCard
                  label="Portfolio corr to SPY"
                  value={fmtRho(result.corrSpy, 3)}
                  sublabel="Daily return correlation"
                />

                <StatCard
                  label="Up when SPY is down"
                  value={fmtPct(result.upWhenSpyDownRate, 1)}
                  sublabel="P(portfolio > 0 | SPY < 0)"
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <StatCard
                  label="Vol vs SPY"
                  value={fmtNum(result.relativeVolToSpy, 3)}
                  sublabel="This drives Volatility score"
                />

                <StatCard
                  label="Diversification ratio"
                  value={fmtNum(result.divRatio, 3)}
                  sublabel="∑wᵢσᵢ / σₚ"
                />

                <StatCard
                  label="Weighted avg correlation"
                  value={fmtRho(result.rhoBar, 3)}
                  sublabel="ρ̄ (pairwise, weight-adjusted)"
                />

                <StatCard
                  label="Effective bets (corr-only)"
                  value={fmtNum(result.neffCorr, 3)}
                  sublabel="1 / (wᵀCorrw)"
                />
              </div>

              {result.missing && result.missing.length > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    borderRadius: 12,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>
                    Missing tickers
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                    A ticker can be missing from correlation matrix (corr),
                    vols.json (vol), expected_returns.json (exp), or
                    daily_returns.csv (daily).
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    {result.missingReasons.map((m) => (
                      <div
                        key={m.ticker}
                        style={{
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 13,
                        }}
                      >
                        {m.ticker}{" "}
                        <span style={{ opacity: 0.7 }}>
                          ({m.missing.join(", ")})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>
                  Weights used (normalized)
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {result.normalizedHoldings.map((h) => (
                    <span
                      key={h.ticker}
                      style={{
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 13,
                        padding: "4px 8px",
                        borderRadius: 999,
                        background: "#f3f4f6",
                      }}
                    >
                      {h.ticker} {h.weightPct.toFixed(2)}%
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>
                  Sector distribution
                </div>

                <div
                  style={{
                    overflowX: "auto",
                    border: "1px solid #f1f5f9",
                    borderRadius: 12,
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#fafafa" }}>
                        <th
                          style={{
                            textAlign: "left",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Sector
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Weight
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sectorWeights.map((s) => (
                        <tr key={s.sector}>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              fontWeight: 700,
                            }}
                          >
                            {s.sector}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              textAlign: "right",
                            }}
                          >
                            {s.weightPct.toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>
                  Risk contributions (variance share)
                </div>

                <div
                  style={{
                    overflowX: "auto",
                    border: "1px solid #f1f5f9",
                    borderRadius: 12,
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#fafafa" }}>
                        <th
                          style={{
                            textAlign: "left",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Ticker
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Sector
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Weight
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Exp (ann)
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Vol (ann)
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Div Yield
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: 10,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Variance %
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.riskTable.map((r) => (
                        <tr key={r.ticker}>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              fontFamily:
                                "ui-monospace, SFMono-Regular, Menlo, monospace",
                              fontWeight: 800,
                            }}
                          >
                            {r.ticker}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                            }}
                          >
                            {r.sector}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              textAlign: "right",
                            }}
                          >
                            {r.weightPct.toFixed(2)}%
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              textAlign: "right",
                            }}
                          >
                            {Number.isFinite(r.expAnnualPct)
                              ? r.expAnnualPct.toFixed(2) + "%"
                              : "—"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              textAlign: "right",
                            }}
                          >
                            {Number.isFinite(r.volAnnualPct)
                              ? r.volAnnualPct.toFixed(2) + "%"
                              : "—"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              textAlign: "right",
                            }}
                          >
                            {Number.isFinite(r.dividendYieldPct)
                              ? r.dividendYieldPct.toFixed(2) + "%"
                              : "—"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderBottom: "1px solid #f1f5f9",
                              textAlign: "right",
                            }}
                          >
                            {Number.isFinite(r.pctVariance)
                              ? (r.pctVariance * 100).toFixed(1) + "%"
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                  Note: Risk contributions are based on annualized covariance
                  implied by (corr × vol × vol). Defense metrics use actual
                  overlapping daily returns versus SPY. Dividend yield is
                  weighted from dividends.json. Sector weights are aggregated
                  from sectors.json. Diversification score is based on effective
                  independent bets (1 / (wᵀCorrw)), scaled from 0 to 100
                  relative to the number of holdings. Volatility Score maps
                  directly from Vol vs SPY with 1.00 = 50. Defensiveness Score
                  maps directly from Downside Capture with 1.00 = 50. Portfolio
                  macro table and radar chart are based on the weighted
                  portfolio’s daily return matched to macro regime dates. The
                  stock-type pie chart classifies each holding by its own best
                  macro regime, then aggregates those classifications by
                  normalized portfolio weight.
                </div>
              </div>

              <details style={{ marginTop: 16 }}>
                <summary style={{ cursor: "pointer" }}>Debug</summary>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    fontSize: 12,
                    marginTop: 8,
                  }}
                >
{JSON.stringify(
  {
    corr_url: CORR_CSV_URL,
    vols_url: VOLS_JSON_URL,
    exp_url: EXPECTED_RETURNS_URL,
    daily_url: DAILY_RETURNS_URL,
    dividends_url: DIVIDENDS_URL,
    sectors_url: SECTORS_URL,
    macro_urls: MACRO_CANDIDATE_URLS,
    vols_as_of: volsJson?.as_of,
    exp_as_of: expJson?.as_of,
    dividends_as_of: divJson?.as_of,
    sectors_as_of: sectorJson?.as_of,
    used_count: result.usedCount,
    overlap_days: result.overlapDays,
    spy_down_days: result.spyDownDays,
    rho_bar: result.rhoBar,
    neff_corr: result.neffCorr,
    diversification_score: result.diversificationScore,
    portfolio_vol_annual: result.volP,
    portfolio_daily_move: result.dailyMove,
    portfolio_return_annual: result.portReturn,
    portfolio_dividend_yield: result.portfolioDividendYield,
    annual_dividend_income: result.annualDividendIncome,
    portfolio_value: portfolioValueNum,
    top_sector: result.topSector,
    effective_sectors: result.effectiveSectors,
    defensive_sector_weight: result.defensiveSectorWeight,
    sector_hhi: result.sectorHHI,
    risk_free_rate: RISK_FREE_RATE,
    sharpe: result.sharpe,
    beta_spy: result.betaSpy,
    corr_spy: result.corrSpy,
    downside_capture: result.downsideCapture,
    up_when_spy_down: result.upWhenSpyDownRate,
    relative_vol_to_spy: result.relativeVolToSpy,
    defensive_score: result.defensiveScore,
    defensive_score_legacy: result.defensiveScoreLegacy,
    defensive_score_50: result.defensiveScore50,
    volatility_score_50: result.volatilityScore50,
    diversification_ratio: result.divRatio,
    portfolio_rsi_14: result.portfolioRSI14,
    portfolio_macro_days: result.portfolioMacroRows?.length || 0,
    portfolio_best_macro_type: result.bestPortfolioMacroType,
    stock_type_pie_data: result.stockTypePieData,
    holding_macro_types: result.holdingMacroTypes,
    last30_portfolio_rows: result.last30PortfolioRows,
    ai_summary_present: Boolean(aiSummary),
  },
  null,
  2
)}
                </pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}



